import { inject, Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { ContextService } from '@services/context';
import { SeoService } from '@services/seo';
import { JsonLD } from '@services/seo/model';
import { Breadcrumb } from '@shared/components/breadcrumb/model';

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  readonly #contextService = inject(ContextService);
  readonly #seoService = inject(SeoService);
  readonly #breadcrumb = signal<Breadcrumb[]>([]);
  readonly breadcrumb = this.#breadcrumb.asReadonly();

  setBreadcrumb(breadcrumb: Breadcrumb[]): void {
    this.#breadcrumb.set(breadcrumb);

    // Empty breadcrumb: remove the JSON-LD and stop. Rebuilding it here would re-inject an empty
    // BreadcrumbList (itemListElement: []), i.e. invalid structured data, cancelling the removal.
    if (breadcrumb.length === 0) {
      this.#seoService.removeJsonLdScript('breadcrumb');
      return;
    }

    if (this.#contextService.isBrowser()) {
      this.#seoService.addJsonLdScript('breadcrumb', this.#buildBreadcrumbJsonLd(breadcrumb));
    }
  }

  #buildBreadcrumbJsonLd(breadcrumb: Breadcrumb[]): JsonLD {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumb.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.title,
        item: `${environment.hostname}${item.url}`,
      })),
    };
  }
}
